import React, { useState, useEffect } from 'react';
import { CheckCircle, Package, Clock, ArrowRight, Home, Eye, Copy, ChevronDown, ChevronUp, BookOpen, ExternalLink, Key, Mail, MessageCircle, HelpCircle, Send } from 'lucide-react';
import { fetchOrderByOrderId } from '../api/orderApi';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { loginByToken, fetchUserProfile, autoLoginWithEmail } from '../store/userSlice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getUserProfile } from '../api/user';

const PaymentSuccessPage = ({ orderData = {} }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isLoggedIn, user } = useSelector((state) => state.user);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderNumberExpanded, setOrderNumberExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [tokenLoginAttempted, setTokenLoginAttempted] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState(''); // 验证码接收邮箱
  const [cardKeys, setCardKeys] = useState([]); // 卡密列表
  const [cardKeysExpanded, setCardKeysExpanded] = useState(true); // 卡密区域展开状态，默认展开

  // 产品类型到教程类型的映射关系
  const productTypeToTutorialType = {
    // A类：基础服务，只需要访问链接
    'gpt': 'a',
    'claude': 'a',
    'midjourney': 'a',
    'chatgpt': 'a',
    
    // B类：需要账户信息的服务
    'netflix': 'b',
    'spotify': 'b',
    'youtube': 'b',
    'office365': 'b',
    'app': 'b',
    'app1': 'b',
    'app3': 'b',
    'app6': 'b',
    // C类：即时生效的服务
    'vpn': 'c',
    'cloud': 'c',
    'api': 'c',
    'license': 'c',
    'renew': 'c',  
    // 默认使用A类
    'default': 'a'
  };

  // 生成带语言参数的服务URL
  const generateServiceUrl = (baseUrl, userToken) => {
    if (!baseUrl || !userToken) return '#';
    
    try {
      const url = new URL(baseUrl);
      url.searchParams.set('userToken', userToken);
      
      // 添加当前系统语言参数
      if (i18n.language) {
        url.searchParams.set('lang', i18n.language);
        console.log('生成服务URL，添加语言参数:', i18n.language);
      }
      
      const finalUrl = url.toString();
      console.log('最终生成的服务URL:', finalUrl);
      return finalUrl;
    } catch (error) {
      console.error('生成服务URL时出错:', error);
      // 如果URL构建失败，回退到原始方式
      const langParam = i18n.language ? `&lang=${i18n.language}` : '';
      const fallbackUrl = `${baseUrl}?userToken=${userToken}${langParam}`;
      console.log('使用回退方式生成的URL:', fallbackUrl);
      return fallbackUrl;
    }
  };

  // 产品类型到登录链接的映射 - 统一使用 chat.openai.com (GPT官网)
  const productLoginUrls = {
    'netflix': 'https://chat.openai.com',
    'spotify': 'https://chat.openai.com',
    'youtube': 'https://chat.openai.com',
    'office365': 'https://chat.openai.com',
    'adobe': 'https://chat.openai.com',
    'gpt': 'https://chat.openai.com',
    'claude': 'https://chat.openai.com',
    'midjourney': 'https://chat.openai.com',
    'chatgpt': 'https://chat.openai.com',
    'app': 'https://chat.openai.com',
    'renew': 'https://chat.openai.com',
    'official': 'https://chat.openai.com',
    'official-renewal': 'https://chat.openai.com',
    'official-shared': 'https://chat.openai.com',
    'shared': 'https://chat.openai.com',
    'default': 'https://chat.openai.com'
  };

  // 邮箱服务商登录链接
  const emailLoginUrls = {
    'gmail.com': 'https://mail.google.com',
    'outlook.com': 'https://outlook.live.com',
    'hotmail.com': 'https://outlook.live.com',
    'yahoo.com': 'https://mail.yahoo.com',
    'qq.com': 'https://mail.qq.com',
    '163.com': 'https://mail.163.com',
    '126.com': 'https://mail.126.com'
  };

  // 服务激活说明配置 - 统一的淡雅配色
  const serviceActivationConfig = {
    'a': {
      title: t('paymentSuccess.userGuide.serviceActivation.title'),
      bgColor: 'from-gray-50 to-gray-50',
      iconBgColor: 'bg-orange-500',
      items: [
        t('paymentSuccess.userGuide.serviceActivation.items.activating'),
        t('paymentSuccess.userGuide.serviceActivation.items.notification')
      ]
    },
    'b': {
      title: t('paymentSuccess.userGuide.accountPreparation.title'),
      bgColor: 'from-orange-50 to-orange-50',
      iconBgColor: 'bg-orange-500',
      items: [
        t('paymentSuccess.userGuide.accountPreparation.items.preparing', { productName: '{{productName}}' }),
        t('paymentSuccess.userGuide.accountPreparation.items.configuring'),
        t('paymentSuccess.userGuide.accountPreparation.items.displayInfo')
      ]
    },
    'c': {
      title: t('paymentSuccess.userGuide.serviceActivated.title'),
      bgColor: 'from-green-50 to-green-50',
      iconBgColor: 'bg-green-600',
      items: [
        t('paymentSuccess.userGuide.serviceActivated.items.activated'),
        t('paymentSuccess.userGuide.serviceActivated.items.followGuide'),
        t('paymentSuccess.userGuide.serviceActivated.items.readyToUse')
      ]
    }
  };

  // 获取邮箱登录链接
  const getEmailLoginUrl = (email) => {
    if (!email) return 'https://mail.google.com';
    const domain = email.split('@')[1];
    return emailLoginUrls[domain] || 'https://mail.google.com';
  };

  // 获取服务激活配置
  const getServiceActivationConfig = (type) => {
    return serviceActivationConfig[type] || serviceActivationConfig['a']; // 默认使用 a 类型配置
  };

  // 从URL获取参数
  const getUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      orderId: urlParams.get('orderId'),
      productType: urlParams.get('productType'),
      productName: urlParams.get('productName'),
      price: urlParams.get('price'),
      version: urlParams.get('version'),
      type: urlParams.get('type'), // 新增type参数
      userToken: urlParams.get('userToken') || urlParams.get('token') // 支持userToken或token参数
    };
  };

  // 自动登录处理
  useEffect(() => {
    const urlParams = getUrlParams();
    const userToken = urlParams.userToken;
    
    // 如果有 userToken 且还没尝试过 token 登录
    if (userToken && !tokenLoginAttempted && !isLoggedIn) {
      setTokenLoginAttempted(true);
      console.log('PaymentSuccessPage: 尝试使用 userToken 自动登录:', userToken);
      
      // 使用 userToken 进行登录
      dispatch(loginByToken(userToken))
        .then((result) => {
          if (loginByToken.fulfilled.match(result)) {
            console.log('PaymentSuccessPage: 自动登录成功');
            // 登录成功后，清除 URL 中的 userToken 参数
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('userToken');
            newSearchParams.delete('token');
            navigate(`${window.location.pathname}?${newSearchParams.toString()}`, { replace: true });
          } else {
            console.error('PaymentSuccessPage: 自动登录失败:', result.error);
          }
        })
        .catch((error) => {
          console.error('PaymentSuccessPage: 自动登录异常:', error);
        });
    }
  }, [searchParams, tokenLoginAttempted, isLoggedIn, dispatch, navigate]);

  // 自动登录功能 - 使用订单邮箱作为uid
  const attemptAutoLogin = async (orderEmail) => {
    console.log('=== attemptAutoLogin 被调用 ===');
    console.log('参数 orderEmail:', orderEmail);
    console.log('当前状态 autoLoginAttempted:', autoLoginAttempted);
    console.log('当前状态 isLoggedIn:', isLoggedIn);
    
    if (!orderEmail || autoLoginAttempted || isLoggedIn) {
      console.log('❌ 自动登录被跳过，原因:');
      console.log('  - 没有邮箱:', !orderEmail);
      console.log('  - 已尝试过:', autoLoginAttempted);
      console.log('  - 已登录:', isLoggedIn);
      return;
    }

    try {
      setAutoLoginAttempted(true);
      console.log('🚀 开始尝试使用订单邮箱自动登录:', orderEmail);
      
      // 使用新的autoLoginWithEmail action
      const result = await dispatch(autoLoginWithEmail(orderEmail));
      console.log('autoLoginWithEmail 结果:', result);
      
      if (autoLoginWithEmail.fulfilled.match(result)) {
        console.log('✅ 支付成功页面备用自动登录成功:', result.payload);
        
        // 显示成功提示
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        
        console.log('🎉 备用自动登录完成，用户状态已更新');
      } else {
        console.log('❌ 支付成功页面备用自动登录失败:', result);
        console.log('失败原因:', result.payload || result.error);
        // 登录失败不影响页面正常显示
      }
      
    } catch (error) {
      console.error('💥 自动登录过程中发生错误:', error);
      // 登录失败不影响页面正常显示，只是不会自动登录
    }
  };

  // 获取订单详情
  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const urlParams = getUrlParams();
        
        if (!urlParams.orderId) {
          throw new Error(t('paymentSuccess.loadError'));
        }

        const response = await fetchOrderByOrderId({ orderId: urlParams.orderId });
        
        // 检查API响应是否有效
        if (!response || (!response.data && !response.order)) {
          throw new Error('订单数据加载失败，请稍后重试');
        }
        
        if (response.data || response.order) {
          // 只从API返回的data.type获取产品类型
          const productType = response.data?.type || response.order?.product_type || 'renew';
          
          // 调试：输出产品类型信息
          console.log('=== 支付成功页面产品类型调试 ===');
          console.log('API返回的 response.data?.type:', response.data?.type);
          console.log('API返回的 response.order?.product_type:', response.order?.product_type);
          console.log('最终确定的 productType:', productType);
          
          // 根据产品类型映射到教程类型（a/b/c）
          const productTypeLower = productType ? productType.toLowerCase() : '';
          const tutorialType = productTypeToTutorialType[productTypeLower] || productTypeToTutorialType['default'];
          
          // 调试：打印完整的API响应
          console.log('=== 订单API完整响应 ===');
          console.log('response.order:', response.order);
          console.log('response.data:', response.data);
          
          // 尝试多种方式获取邮箱
          const possibleEmails = [
            response.order?.uid,
            response.order?.user_email, 
            response.order?.email,
            response.order?.customer_email,
            response.data?.uid,
            response.data?.user_email,
            response.data?.email
          ];
          
          console.log('可能的邮箱字段:', possibleEmails);
          const orderEmail = possibleEmails.find(email => email && email.includes('@'));
          console.log('最终提取的邮箱:', orderEmail);
          
          // 转换API数据为组件所需格式
          const rawProductName = response.data?.name || response.order?.product_name || '';
          // 如果产品名称是翻译key，则进行翻译处理
          const displayProductName = rawProductName.startsWith('sku.') ? t(rawProductName) : rawProductName;
          
          const orderInfo = {
            orderNumber: response.order?.out_trade_no || response.data?.orderNumber || urlParams.orderId,
            orderNo: response.order?.orderNo || response.data?.orderNo || '',
            productName: displayProductName,
            totalAmount: parseFloat(response.data?.product_price_usd || response.order?.amount || '0'),
            paymentMethod: response.order?.payment_channel || response.data?.payment_method || '',
            orderDate: response.order?.created_at ? new Date(response.order.created_at).toLocaleString('zh-CN') : new Date().toLocaleString('zh-CN'),
            productType: productType,
            productId: response.data?.id || response.order?.product_id || '',
            orderStatus: response.order?.order_status || response.data?.status || 'success',
            createdAt: response.order?.created_at || response.data?.created_at || new Date().toISOString(),
            type: tutorialType, // 使用映射后的教程类型
            // 账户信息（用于b类型）
            accountName: response.order?.account_name || response.data?.account_name || '',
            accountPassword: response.order?.account_password || response.data?.account_password || '',
            emailPassword: response.order?.email_password || response.data?.email_password || '',
            // 添加订单邮箱信息
            orderEmail: orderEmail
          };
          
          console.log('构建的订单信息:', orderInfo);
          console.log('当前登录状态:', isLoggedIn);
          console.log('自动登录尝试状态:', autoLoginAttempted);
          
          setOrder(orderInfo);
          
          // 处理卡密信息
          if (response.query_key_result && Array.isArray(response.query_key_result)) {
            console.log('=== 卡密信息处理 ===');
            console.log('原始卡密数据:', response.query_key_result);
            setCardKeys(response.query_key_result);
          } else {
            setCardKeys([]);
          }
          
          // 检查是否需要自动登录（作为备用方案）
          if (!isLoggedIn && orderInfo.orderEmail) {
            console.log('🔄 支付成功页面检测到未登录用户，尝试备用自动登录:', orderInfo.orderEmail);
            await attemptAutoLogin(orderInfo.orderEmail);
          } else {
            console.log('✅ 用户状态正常:');
            console.log('  - 用户已登录:', isLoggedIn);
            console.log('  - 订单邮箱:', orderInfo.orderEmail);
          }
        }
      } catch (err) {
        console.error('获取订单详情失败:', err);
        setError(err.message || '获取订单详情失败');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [isLoggedIn, autoLoginAttempted]); // 添加依赖项

  // 如果正在加载
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('paymentSuccess.loading')}</p>
        </div>
      </div>
    );
  }

  // 如果加载失败
  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 text-red-500 p-8 rounded-xl">
            <p className="text-lg font-semibold mb-2">{t('paymentSuccess.loadFailed')}</p>
            <p>{error || t('paymentSuccess.loadError')}</p>
          </div>
        </div>
      </div>
    );
  }

  const handleViewOrder = () => {
    // 跳转到订单详情页
    console.log('查看订单详情');
  };

  const handleContinueShopping = () => {
    // 跳转到首页或商品页
    console.log('继续购物');
  };

  const handleBackHome = () => {
    // 返回首页
    console.log('返回首页');
  };

  const handleStartUsing = () => {
    // 跳转到AI Fleet页面
    navigate('/ai-fleet');
  };

  // 卡密脱敏显示函数
  const maskCardKey = (cardKey) => {
    if (!cardKey || cardKey.length <= 8) return cardKey;
    const start = cardKey.substring(0, 4);
    const end = cardKey.substring(cardKey.length - 4);
    const middle = '*'.repeat(Math.min(cardKey.length - 8, 8)); // 最多显示8个星号
    return `${start}${middle}${end}`;
  };

  // 批量复制所有卡密
  const copyAllCardKeys = async () => {
    if (cardKeys.length === 0) return;
    
    const allKeys = cardKeys.map(key => key.card_key_display).join('\n');
    try {
      await navigator.clipboard.writeText(allKeys);
      setCopySuccess('allCards');
      setShowToast(true);
      setTimeout(() => {
        setCopySuccess('');
        setShowToast(false);
      }, 2000);
    } catch (err) {
      console.error('批量复制失败:', err);
    }
  };

  // 复制文本到剪贴板 - 优化反馈
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(type);
      setShowToast(true);
      setTimeout(() => {
        setCopySuccess('');
        setShowToast(false);
      }, 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 截断长文本
  const truncateText = (text, maxLength = 20) => {
    if (text.length <= maxLength) return text;
    return orderNumberExpanded ? text : text.substring(0, maxLength) + '...';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 成功状态区域 - 优化后的设计 */}
      <section className="relative bg-gradient-to-b from-orange-500 to-orange-600 overflow-hidden">
        {/* 装饰性背景元素 */}
        <div className="absolute inset-0">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-orange-400 rounded-full opacity-20 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-400 rounded-full opacity-20 blur-3xl"></div>
        </div>
        
        <div className="relative container-page py-12">
          <div className="text-center">
            {/* 成功图标 - 更精致的动画效果 */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-white rounded-full animate-ping opacity-20"></div>
                <div className="relative bg-white rounded-full p-4 shadow-xl">
                  <CheckCircle className="w-12 h-12 text-orange-500" />
                </div>
              </div>
            </div>

            {/* 成功标题 */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              {t('paymentSuccess.title')}
            </h1>
            
            <p className="text-lg text-orange-50 max-w-md mx-auto">
              {t('paymentSuccess.subtitle')}
            </p>
            
            {/* 订单金额展示 */}
            <div className="mt-6 inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 text-white">
              <span className="text-sm opacity-90">{t('paymentSuccess.paymentAmount')}</span>
              <span className="ml-2 text-2xl font-bold">${order?.totalAmount?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* 主要内容区域 */}
      <section className="section-standard">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* 左侧：订单详情 (1/3) */}
            <div className="lg:col-span-1">
              {/* 订单信息卡片 - 更精简的设计 */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center">
                    <Package className="w-5 h-5 text-gray-700 mr-2" />
                    <h2 className="text-lg font-semibold text-gray-900">{t('paymentSuccess.orderInfo.title')}</h2>
                  </div>
                </div>
                
                <div className="px-6 py-4">
                  {/* 订单基本信息 - 优化间距 */}
                  <div className="space-y-0">
                    <div className="flex justify-between items-center py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors rounded-lg px-2 -mx-2">
                      <span className="text-sm text-gray-500">{t('paymentSuccess.orderInfo.orderNumber')}</span>
                      <div className="flex items-center space-x-1">
                        <span className="font-medium text-gray-900 text-sm font-mono">
                          {truncateText(order.orderNumber, 16)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(order.orderNumber, 'orderNumber')}
                          className="p-1 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded transition-all"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors rounded-lg px-2 -mx-2">
                      <span className="text-sm text-gray-500">{t('paymentSuccess.orderInfo.productName')}</span>
                      <span className="font-medium text-gray-900 text-sm">{order.productName}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors rounded-lg px-2 -mx-2">
                      <span className="text-sm text-gray-500">{t('paymentSuccess.orderInfo.productType')}</span>
                      <span className="font-medium text-gray-900 text-sm uppercase">{order.productType}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 bg-orange-50 rounded-lg px-3 -mx-1 my-2">
                      <span className="text-sm text-gray-600 font-medium">{t('paymentSuccess.orderInfo.orderAmount')}</span>
                      <span className="font-bold text-xl text-orange-600">
                        ${order.totalAmount.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors rounded-lg px-2 -mx-2">
                      <span className="text-sm text-gray-500">{t('paymentSuccess.orderInfo.paymentChannel')}</span>
                      <span className="font-medium text-gray-900 text-sm">{order.paymentMethod}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors rounded-lg px-2 -mx-2">
                      <span className="text-sm text-gray-500">{t('paymentSuccess.orderInfo.orderTime')}</span>
                      <span className="font-medium text-gray-900 text-sm">{order.orderDate}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <span className="text-gray-600">{t('paymentSuccess.orderInfo.merchantOrderNo')}</span>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-gray-900 text-sm">{order.orderNo}</span>
                        <button
                          onClick={() => copyToClipboard(order.orderNo, 'orderNo')}
                          className="text-gray-400 hover:text-orange-500 transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        {copySuccess === 'orderNo' && (
                          <span className="text-green-500 text-xs">{t('paymentSuccess.orderInfo.copied')}</span>
                        )}
                      </div>
                    </div>
                    
                    {/* B类产品显示账户信息 */}
                    {order.type === 'b' && (
                      <>
                        <div className="flex justify-between items-center py-3 border-b border-gray-100">
                          <span className="text-gray-600">{t('paymentSuccess.orderInfo.loginEmail')}</span>
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-gray-900 text-sm truncate max-w-[150px]">
                              {order.accountName || t('paymentSuccess.orderInfo.preparing')}
                            </span>
                            {order.accountName && (
                              <>
                                <button
                                  onClick={() => copyToClipboard(order.accountName, 'accountNameLeft')}
                                  className="text-gray-400 hover:text-orange-500 transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                {copySuccess === 'accountNameLeft' && (
                                  <span className="text-green-500 text-xs">{t('paymentSuccess.orderInfo.copied')}</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center py-3 border-b border-gray-100">
                          <span className="text-gray-600">{t('paymentSuccess.orderInfo.loginPassword')}</span>
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-gray-900 text-sm font-mono">
                              {order.accountPassword || t('paymentSuccess.orderInfo.preparing')}
                            </span>
                            {order.accountPassword && (
                              <>
                                <button
                                  onClick={() => copyToClipboard(order.accountPassword, 'accountPasswordLeft')}
                                  className="text-gray-400 hover:text-orange-500 transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                {copySuccess === 'accountPasswordLeft' && (
                                  <span className="text-green-500 text-xs">{t('paymentSuccess.orderInfo.copied')}</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center py-3 border-b border-gray-100">
                          <span className="text-gray-600">{t('paymentSuccess.orderInfo.emailPassword')}</span>
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-gray-900 text-sm font-mono">
                              {order.emailPassword || t('paymentSuccess.orderInfo.preparing')}
                            </span>
                            {order.emailPassword && (
                              <>
                                <button
                                  onClick={() => copyToClipboard(order.emailPassword, 'emailPasswordLeft')}
                                  className="text-gray-400 hover:text-orange-500 transition-colors"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                {copySuccess === 'emailPasswordLeft' && (
                                  <span className="text-green-500 text-xs">{t('paymentSuccess.orderInfo.copied')}</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* 卡密信息显示 */}
                    {cardKeys.length > 0 && (
                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <Key className="w-5 h-5 text-gray-700 mr-2" />
                            <h3 className="text-md font-semibold text-gray-900">
                              卡密信息 ({cardKeys.length}张)
                            </h3>
                          </div>
                          <div className="flex items-center space-x-2">
                            {cardKeys.length > 1 && (
                              <button
                                onClick={copyAllCardKeys}
                                className="px-3 py-1.5 text-xs bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors flex items-center space-x-1"
                              >
                                <Copy className="w-3 h-3" />
                                <span>批量复制</span>
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* 卡密列表 - 直接显示所有 */}
                        <div className="space-y-2">
                          {cardKeys.map((cardKey, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-gray-500 font-medium">#{index + 1}</span>
                                  <span className="font-mono text-sm text-gray-900">
                                    {cardKey.card_key_display}
                                  </span>
                                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                    cardKey.active_status === 1 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {cardKey.active_status === 1 ? '已激活' : '未激活'}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => copyToClipboard(cardKey.card_key_display, `cardKey_${index}`)}
                                className="p-1.5 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded transition-all ml-2"
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                              {copySuccess === `cardKey_${index}` && (
                                <span className="text-green-500 text-xs ml-2">已复制</span>
                              )}
                            </div>
                          ))}
                        </div>
                        
                        {/* 批量复制成功提示 */}
                        {copySuccess === 'allCards' && (
                          <div className="mt-2 text-center text-green-500 text-sm">
                            已复制所有卡密到剪贴板
                          </div>
                        )}
                      </div>
                    )}
                  </div>


                </div>
              </div>
            </div>

            {/* 右侧：用户指导和操作 (2/3) */}
            <div className="lg:col-span-2 space-y-6">

              {/* 使用教程 - 简洁设计 */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                {/* 教程头部 - 与主题色统一 */}
                <div className="px-6 py-5 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <BookOpen className="w-5 h-5 text-gray-700 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        {t('paymentSuccess.userGuide.title')}
                      </h3>
                    </div>
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {order.productName}
                    </span>
                  </div>
                </div>
                
                <div className="p-8">
                  {/* 根据type显示不同的使用指南 */}
                  
                  {/* A类型：基础使用说明 */}
                  {order.type === 'a' && (
                    <div className="space-y-6">
                      <div className={`bg-gradient-to-r ${serviceActivationConfig[order.type].bgColor} rounded-2xl p-6`}>
                        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                          <div className={`w-10 h-10 ${serviceActivationConfig[order.type].iconBgColor} rounded-xl mr-4 flex items-center justify-center`}>
                            <span className="text-white font-bold">1</span>
                          </div>
                          {serviceActivationConfig[order.type].title}
                        </h4>
                        <ul className="space-y-3 text-gray-700">
                          {serviceActivationConfig[order.type].items.map((item, index) => (
                            <li key={index} className="flex items-start">
                              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* B类型：包含账户信息 */}
                  {order.type === 'b' && (
                    <div className="space-y-6">
                      {/* 服务说明 */}
                      <div className={`bg-gradient-to-r ${serviceActivationConfig[order.type].bgColor} rounded-2xl p-6`}>
                        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                          <div className={`w-10 h-10 ${serviceActivationConfig[order.type].iconBgColor} rounded-xl mr-4 flex items-center justify-center`}>
                            <span className="text-white font-bold">1</span>
                          </div>
                          {serviceActivationConfig[order.type].title}
                        </h4>
                        <ul className="space-y-3 text-gray-700">
                          {serviceActivationConfig[order.type].items.map((item, index) => (
                            <li key={index} className="flex items-start">
                              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                              <span>{item.replace('{{productName}}', order.productName)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                                            <h4 className="text-2xl font-bold text-gray-900 text-center mb-4">{t('paymentSuccess.userGuide.accountInfo.title')}</h4>
                      
                      {/* 1. 登录邮箱 */}
                      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1 min-w-0">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center mr-3 flex-shrink-0">
                              <Mail className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-500 mb-1">{t('paymentSuccess.orderInfo.loginEmail')}</p>
                              <p className="font-bold text-lg text-gray-900 truncate">
                                {order.accountName || t('paymentSuccess.orderInfo.preparing')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => order.accountName && copyToClipboard(order.accountName, 'accountName')}
                              className={`p-2 rounded-lg transition-colors group relative ${
                                order.accountName 
                                  ? 'bg-gray-100 hover:bg-gray-200 cursor-pointer' 
                                  : 'bg-gray-50 cursor-not-allowed opacity-50'
                              }`}
                              title="复制邮箱"
                              disabled={!order.accountName}
                            >
                              <Copy className={`w-4 h-4 ${order.accountName ? 'text-gray-600 group-hover:text-gray-800' : 'text-gray-400'}`} />
                              {copySuccess === 'accountName' && (
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-green-500 text-xs whitespace-nowrap bg-white px-2 py-1 rounded shadow-lg">✓ {t('paymentSuccess.orderInfo.copied')}</span>
                              )}
                            </button>
                            <a 
                              href={order.accountName ? (productLoginUrls[order.productType?.toLowerCase()] || 'https://chat.openai.com') : '#'} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className={`inline-flex items-center px-4 py-2 rounded-lg transition-all font-semibold text-sm ${
                                order.accountName
                                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              }`}
                              onClick={(e) => !order.accountName && e.preventDefault()}
                            >
                              <ExternalLink className="w-4 h-4 mr-1.5" />
                              {t('paymentSuccess.userGuide.accountInfo.login')}
                            </a>
                          </div>
                        </div>
                      </div>
                      
                      {/* 2. 登录密码 */}
                      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1 min-w-0">
                            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mr-3 flex-shrink-0">
                              <Key className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-500 mb-1">{t('paymentSuccess.orderInfo.loginPassword')}</p>
                              <p className="font-bold text-lg text-gray-900 font-mono truncate">
                                {order.accountPassword || t('paymentSuccess.orderInfo.preparing')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => order.accountPassword && copyToClipboard(order.accountPassword, 'accountPassword')}
                              className={`p-2 rounded-lg transition-colors group relative ${
                                order.accountPassword 
                                  ? 'bg-gray-100 hover:bg-gray-200 cursor-pointer' 
                                  : 'bg-gray-50 cursor-not-allowed opacity-50'
                              }`}
                              title="复制密码"
                              disabled={!order.accountPassword}
                            >
                              <Copy className={`w-4 h-4 ${order.accountPassword ? 'text-gray-600 group-hover:text-gray-800' : 'text-gray-400'}`} />
                              {copySuccess === 'accountPassword' && (
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-green-500 text-xs whitespace-nowrap bg-white px-2 py-1 rounded shadow-lg">✓ {t('paymentSuccess.orderInfo.copied')}</span>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* 邮箱密码卡片 - 移除登录按钮 */}
                      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1 min-w-0">
                            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center mr-3 flex-shrink-0">
                              <Mail className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-500 mb-1">{t('paymentSuccess.orderInfo.emailPassword')}</p>
                              <p className="font-bold text-lg text-gray-900 font-mono truncate">
                                {order.emailPassword || t('paymentSuccess.orderInfo.preparing')}
                              </p>
                            </div>
                          </div>
                          {/* 移除登录按钮，只保留复制按钮 */}
                          {order.emailPassword && (
                            <button
                              onClick={() => copyToClipboard(order.emailPassword, 'emailPassword')}
                              className="text-gray-400 hover:text-orange-500 transition-colors"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {/* 邮件验证码接收地址 - 仅对共享账号显示，排在邮箱密码后面 */}
                      {(order.productType === 'official-shared' || order.productType?.includes('shared') || order.productType === 'app3' || order.productType === 'app6') && (
                        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center flex-1 min-w-0">
                              <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mr-3 flex-shrink-0">
                                <MessageCircle className="w-5 h-5 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-amber-700 mb-1 font-medium">{t('paymentSuccess.orderInfo.verificationEmailAddress')}</p>
                                <p className="font-bold text-lg text-blue-600 truncate break-all">
                                  {order.verificationUrl || order.verificationEmail || order.accountVerificationEmail || 'http://gpt-verify-code.iamright.top/'}
                                </p>
                                <p className="text-xs text-amber-600 mt-1">
                                  {t('paymentSuccess.orderInfo.verificationEmailDescription')}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => {
                                  const urlToCopy = order.verificationUrl || order.verificationEmail || order.accountVerificationEmail || 'http://gpt-verify-code.iamright.top/';
                                  copyToClipboard(urlToCopy, 'verificationEmail');
                                }}
                                className="p-2 rounded-lg transition-colors group relative bg-amber-100 hover:bg-amber-200 cursor-pointer"
                                title={t('paymentSuccess.orderInfo.copyVerificationUrl')}
                              >
                                <Copy className="w-4 h-4 text-amber-600 group-hover:text-amber-800" />
                                {copySuccess === 'verificationEmail' && (
                                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-green-500 text-xs whitespace-nowrap bg-white px-2 py-1 rounded shadow-lg">✓ {t('paymentSuccess.orderInfo.copied')}</span>
                                )}
                              </button>
                              <a
                                href={order.verificationUrl || order.verificationEmail || order.accountVerificationEmail || 'http://gpt-verify-code.iamright.top/'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-4 py-2 rounded-lg transition-all font-semibold text-sm bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"
                              >
                                <ExternalLink className="w-4 h-4 mr-1.5" />
                                {t('paymentSuccess.orderInfo.visit')}
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* 卡密信息卡片 - 显示在账户信息区域 */}
                      {cardKeys.length > 0 && (
                        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center mr-3 flex-shrink-0">
                                <Key className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="text-sm text-purple-700 mb-1 font-medium">卡密信息</p>
                                <p className="font-bold text-lg text-gray-900">
                                  共 {cardKeys.length} 张卡密
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {cardKeys.length > 1 && (
                                <button
                                  onClick={copyAllCardKeys}
                                  className="px-3 py-1.5 text-xs bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors flex items-center space-x-1"
                                >
                                  <Copy className="w-3 h-3" />
                                  <span>批量复制</span>
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* 卡密列表 - 显示所有卡密 */}
                          <div className="space-y-3">
                            {cardKeys.map((cardKey, index) => (
                              <div key={index} className="bg-white rounded-lg p-4 border border-purple-100 hover:border-purple-200 transition-all">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-2">
                                      <span className="text-xs text-purple-600 font-medium bg-purple-100 px-2 py-1 rounded-full">
                                        #{index + 1}
                                      </span>
                                      <span className="font-mono text-sm text-gray-900 font-bold">
                                        {cardKey.card_key_display}
                                      </span>
                                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                        cardKey.active_status === 1 
                                          ? 'bg-green-100 text-green-700 border border-green-200' 
                                          : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                      }`}>
                                        {cardKey.active_status === 1 ? '✅ 已激活' : '⏳ 未激活'}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      激活状态：<span className={cardKey.active_status === 1 ? 'text-green-600 font-medium' : 'text-yellow-600 font-medium'}>
                                        {cardKey.active_status === 1 ? '可以正常使用' : '需要点击激活按钮'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 ml-4">
                                    <button
                                      onClick={() => copyToClipboard(cardKey.card_key_display, `cardKey_${index}`)}
                                      className="p-2 rounded-lg transition-colors group relative bg-purple-100 hover:bg-purple-200 cursor-pointer"
                                      title="复制卡密"
                                    >
                                      <Copy className="w-4 h-4 text-purple-600 group-hover:text-purple-800" />
                                      {copySuccess === `cardKey_${index}` && (
                                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-green-500 text-xs whitespace-nowrap bg-white px-2 py-1 rounded shadow-lg">✓ 已复制</span>
                                      )}
                                    </button>
                                    {cardKey.active_status !== 1 && (
                                      <button
                                        onClick={() => navigate('/settings')}
                                        className="px-3 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all font-semibold flex items-center gap-1 shadow-sm"
                                      >
                                        <span>🔑</span>
                                        前往激活
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* 批量复制成功提示 */}
                          {copySuccess === 'allCards' && (
                            <div className="mt-3 text-center text-green-600 text-sm font-medium bg-green-50 py-2 rounded-lg">
                              ✅ 已复制所有卡密到剪贴板
                            </div>
                          )}
                          
                          {/* 重要提示 */}
                          <div className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-white text-xs">💡</span>
                              </div>
                              <div className="text-sm">
                                <p className="text-blue-800 font-medium mb-1">重要提示：</p>
                                <p className="text-blue-700 text-xs leading-relaxed">
                                  • 请妥善保管卡密信息，建议复制保存到安全位置<br/>
                                  • 未激活的卡密需要先激活才能使用相应服务<br/>
                                  • 您也可以在<span className="font-medium">设置页面</span>查看和管理所有卡密
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* 使用提示 */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-6">
                        <p className="text-amber-800 text-sm">
                          <span className="font-bold">{t('paymentSuccess.userGuide.accountInfo.usageTip')}</span>
                          {t('paymentSuccess.userGuide.accountInfo.usageTipContent')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* C类型：其他服务说明 */}
                  {order.type === 'c' && (
                    <div className="space-y-6">
                      <div className={`bg-gradient-to-r ${serviceActivationConfig[order.type].bgColor} rounded-2xl p-6`}>
                        <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                          <div className={`w-10 h-10 ${serviceActivationConfig[order.type].iconBgColor} rounded-xl mr-4 flex items-center justify-center`}>
                            <span className="text-white font-bold">1</span>
                          </div>
                          {serviceActivationConfig[order.type].title}
                        </h4>
                        <ul className="space-y-3 text-gray-700">
                          {serviceActivationConfig[order.type].items.map((item, index) => (
                            <li key={index} className="flex items-start">
                              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* 默认情况（如果type不是a/b/c） */}
                  {(!order.type || !['a', 'b', 'c'].includes(order.type)) && (
                    <div className="bg-gray-50 rounded-2xl p-8 text-center">
                      <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                        <BookOpen className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-600">{t('common.loading')}</p>
                    </div>
                  )}

                  {/* 客服支持区域 */}
                  <div className="mt-8 pt-8 border-t border-gray-200">
                    {/* 操作按钮区域 */}
                    <div className="flex justify-center mb-8">
                      <button
                        onClick={handleStartUsing}
                        className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold text-lg rounded-2xl hover:from-orange-600 hover:to-orange-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                      >
                        <ArrowRight className="w-6 h-6 mr-2" />
                        开始使用
                      </button>
                    </div>
                    
                    <h4 className="text-lg font-bold text-gray-900 mb-6 text-center">{t('paymentSuccess.support.title')}</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {/* 邮件支持 */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center mr-3">
                            <Mail className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h5 className="font-bold text-gray-900">{t('paymentSuccess.support.email.title')}</h5>
                            <p className="text-sm text-gray-600">{t('paymentSuccess.support.email.subtitle')}</p>
                          </div>
                        </div>
                        <a 
                          href="mailto:support@smarttech.com?subject=订单咨询"
                          className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-sm"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          {t('paymentSuccess.support.email.action')}
                        </a>
                      </div>
                      
                      {/* 在线客服 */}
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center mr-3">
                            <MessageCircle className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h5 className="font-bold text-gray-900">{t('paymentSuccess.support.chat.title')}</h5>
                            <p className="text-sm text-gray-600">{t('paymentSuccess.support.chat.subtitle')}</p>
                          </div>
                        </div>
                        <button 
                          onClick={() => console.log('打开客服')}
                          className="inline-flex items-center text-green-600 hover:text-green-700 font-semibold text-sm"
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          {t('paymentSuccess.support.chat.action')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
      </section>

      {/* Toast 通知 - 复制成功反馈和自动登录提示 */}
      {showToast && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-out">
          <div className="bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-sm font-medium">
              {copySuccess ? t('paymentSuccess.toast.copySuccess') : '自动登录成功！您现在已登录到系统'}
            </span>
          </div>
        </div>
      )}

    </div>
  );
};

export default PaymentSuccessPage;
